// @coverage-include-exempt: thin hook over useExperiment (a latch plus a
// timer), the same shape as ad-format-experiment.ts. Its behaviour (control
// fallback, no late flip) is asserted through
// tests/component/monetization/ad-wall-host.test.tsx.
"use client";

import { useEffect, useState } from "react";
import { useExperiment } from "@/lib/analytics/use-experiment";
import { CLIENT_RESOLVE_TIMEOUT_MS } from "@/lib/net/fetch-with-timeout";
import {
	AD_WALL_ARMS,
	AD_WALL_FLAG,
	type AdWallArm,
	toAdWallArm,
} from "./ad-wall";

const VARIANTS = Object.fromEntries(
	AD_WALL_ARMS.map((arm) => [arm, arm]),
) as Record<AdWallArm, AdWallArm>;

/**
 * The visitor's `ad-wall-placement` arm. Flag absent, PostHog blocked, or
 * unresolved after `CLIENT_RESOLVE_TIMEOUT_MS` decide `control` (no wall), and
 * the decision LATCHES so a late flag never pops a wall mid-read.
 */
export function useAdWallArm(): { ready: boolean; arm: AdWallArm } {
	// Exposure fires as `ad_wall_eligible` from the host, only for visitors who
	// meet the wall's conditions, so every arm shares one denominator.
	const { variantKey, isReady } = useExperiment<AdWallArm>({
		flagKey: AD_WALL_FLAG,
		variants: VARIANTS,
		trackExposure: false,
	});
	const [decided, setDecided] = useState<AdWallArm | null>(null);

	useEffect(() => {
		if (isReady) setDecided((prev) => prev ?? toAdWallArm(variantKey));
	}, [isReady, variantKey]);

	useEffect(() => {
		const timer = setTimeout(
			() => setDecided((prev) => prev ?? "control"),
			CLIENT_RESOLVE_TIMEOUT_MS,
		);
		return () => clearTimeout(timer);
	}, []);

	return { ready: decided !== null, arm: decided ?? "control" };
}
